import React, { useState, useEffect } from 'react';
import { trackPromise } from 'react-promise-tracker';
import { Alert } from 'vetrf-ui';
import { EntryType, FindFnParams, PaginationResponse } from 'components/registry/types/api';
import { RegistryWrapper } from 'components/registry/components/RegistryWrapper';
import { FiltersWrapper } from 'components/registry/components/FiltersWrapper';
import { subHeaderTitle } from 'components/registry/components/RegistryWrapper/consts';
import { RegistrySorting } from 'components/registry/components/RegistrySorting';
import { RegistryTable } from 'components/registry/components/RegistryTable';
import { useFiltersContext } from 'components/FiltersContext/context';
import { feedApi } from '../../api';
import { FeedDto, FeedRegistryParams } from '../../api/params';
import { RegistryFilters } from '../../components/Filters/Registry';
import { RegistryTableCardsWrapper } from '../../components/TableCardsWrapper/Registry';
import { defaultParams, excelFields, groupByField, sortingOptions } from './consts';
import { normalizeRegistryFilters } from '../../components/Filters/utils';
import { FeedRegistryFilter } from '../../components/Filters/types';

const FeedRegistry = () => {
  const { filters } = useFiltersContext();
  const [tableData, setTableData] = useState<PaginationResponse<FeedDto> | null>(null);
  const [findParams, setFindParams] = useState<FindFnParams<FeedRegistryParams>>({
    ...defaultParams,
    extendedQuery: filters.feedRegistry
      ? normalizeRegistryFilters(filters.feedRegistry as FeedRegistryFilter)
      : undefined,
  });
  const [error, setError] = useState('');

  const fetchTableData = async (params: FindFnParams<FeedRegistryParams>) => {
    setError('');
    await feedApi
      .findRegistry(params)
      .then(setTableData)
      .catch((err) => setError(err.response.data.message));
  };

  const updateData = async (params: FindFnParams<FeedRegistryParams>) => {
    trackPromise(fetchTableData(params));
  };

  useEffect(() => {
    updateData(findParams);
  }, [findParams]);

  const handleParamChange = (
    values: Partial<FindFnParams<FeedRegistryParams>>,
    resetPagination = false
  ) => {
    setFindParams((prev) =>
      resetPagination
        ? {
            ...prev,
            ...values,
            pageDetails: defaultParams.pageDetails,
          }
        : {
            ...prev,
            ...values,
          }
    );
  };

  const handleDelete = (id: string) => {
    setError('');
    feedApi
      .deleteFeedRequest(id)
      .then(() => {
        if (tableData?.itemList.length === 1 && findParams.pageDetails.pageNumber !== 1) {
          setFindParams((prev) => ({
            ...prev,
            pageDetails: { ...prev.pageDetails, pageNumber: prev.pageDetails.pageNumber - 1 },
          }));
          return;
        }
        updateData(findParams);
      })
      .catch((err) => setError(err.response.data.message));
  };

  return (
    <RegistryWrapper
      registryName="Кормовые добавки"
      subRegistryName="Государственный реестр кормовых добавок"
      subHeader="Государственный реестр кормовых добавок"
      header={subHeaderTitle[EntryType.registry]}
    >
      <FiltersWrapper>
        <RegistryFilters onFiltersChange={(values) => handleParamChange(values, true)} />
      </FiltersWrapper>
      <RegistrySorting
        onSortingChange={(values) => handleParamChange(values, true)}
        sortingOptions={sortingOptions}
        defaultOptions={defaultParams.sortOrders![0]}
        excelFields={excelFields}
        findParams={findParams}
        groupByField={groupByField}
      />
      {error && (
        <Alert closable messageType="danger">
          {error}
        </Alert>
      )}
      <RegistryTable tableData={tableData} handleParamChange={handleParamChange}>
        {!!tableData && <RegistryTableCardsWrapper data={tableData} handleDelete={handleDelete} />}
      </RegistryTable>
    </RegistryWrapper>
  );
};

export default FeedRegistry;
